import { useState } from 'react';
import { Helmet } from 'react-helmet';
import SectionCTA from '../components/SectionCTA';
import Surface from '../components/ui/Surface';
import ContactMap from '../components/ContactMap';
import Button from '../components/ui/Button';
import { submitFormPayload } from '../utils/submitForm';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  question: '',
  comments: ''
};

export default function ContactPage() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState(null);

  const officeAddress = '100 N Tampa St, Tampa, FL 33602';
  const officePhone = '(813) 555-0123';
  const officeEmail = 'hello@carteram.com';

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.phone.trim()) {
      setStatus({ type: 'error', message: 'Please complete the required fields so we can follow up.' });
      return;
    }
    if (!emailPattern.test(form.email)) {
      setStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    setStatus({ type: 'loading', message: 'Submitting your request…' });

    const response = await submitFormPayload({
      formName: 'Contact – No-Cost Consultation',
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      question: form.question,
      comments: form.comments
    });

    if (response?.success) {
      setStatus({ type: 'success', message: 'Thank you! A member of our team will reach out within one business day.' });
      setForm(initialForm);
    } else {
      console.error('Form submit failed', response);
      setStatus({ type: 'error', message: 'Something went wrong submitting your request. Please try again in a moment.' });
    }
  };

  const canSubmit =
    form.firstName.trim() && form.lastName.trim() && form.phone.trim() && emailPattern.test(form.email);


  return (
    <main id="contact">
      <Helmet>
        <title>Contact & Schedule | Carter Asset Management</title>
      </Helmet>
      <section className="section-container py-10 md:py-16">
        <div className="grid gap-8 md:gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div className="space-y-5">
            <p className="text-sm font-semibold uppercase tracking-wide text-cam-blue/70">Contact</p>
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight text-cam-blue">
              Schedule a call or request a second opinion.
            </h1>
            <p className="text-base leading-relaxed text-slate-600">
              Choose the option that fits you best: schedule a call, request a second opinion on your current plan, or begin the
              no-cost consultation. Replace placeholders with your scheduling links.
            </p>
            <Surface className="bg-cam-blue/5 p-6 text-sm text-slate-600">
              <p className="font-semibold text-cam-blue">Office</p>
              <p className="mt-2">{officeAddress}</p>
              <p className="mt-2">Phone: {officePhone}</p>
              <p className="mt-2">
                Email{' '}
                <a href={`mailto:${officeEmail}`} className="text-cam-blue underline">
                  {officeEmail}
                </a>
              </p>
              <p className="mt-4 text-sm leading-relaxed">
                Virtual meetings available nationwide where registered. Please reference Form CRS for jurisdictional information.
              </p>
            </Surface>
          </div>
          <Surface className="p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-semibold text-cam-blue">Start a No-Cost Consultation</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600 md:text-base">
              Share context on your goals, timeline, and comfort with risk. We will review and reach out to see how we can be of service.
            </p>
            <form className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleSubmit} noValidate>
              <div>
                <label htmlFor="firstName" className="text-sm text-slate-700">
                  First Name *
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  required
                  value={form.firstName}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg ring-1 ring-slate-200 px-3 py-3 md:py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-cam-blue"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="text-sm text-slate-700">
                  Last Name *
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  required
                  value={form.lastName}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg ring-1 ring-slate-200 px-3 py-3 md:py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-cam-blue"
                />
              </div>
              <div>
                <label htmlFor="email" className="text-sm text-slate-700">
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg ring-1 ring-slate-200 px-3 py-3 md:py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-cam-blue"
                />
              </div>
              <div>
                <label htmlFor="phone" className="text-sm text-slate-700">
                  Phone Number *
                </label>
                <input
                  id="phone"
                  name="phone"
                  inputMode="tel"
                  required
                  value={form.phone}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg ring-1 ring-slate-200 px-3 py-3 md:py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-cam-blue"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="question" className="text-sm text-slate-700">
                  One question you would like answered
                </label>
                <textarea
                  id="question"
                  name="question"
                  rows="3"
                  value={form.question}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg ring-1 ring-slate-200 px-3 py-3 md:py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-cam-blue"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="comments" className="text-sm text-slate-700">
                  Any other comments, questions, or concerns
                </label>
                <textarea
                  id="comments"
                  name="comments"
                  rows="3"
                  value={form.comments}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg ring-1 ring-slate-200 px-3 py-3 md:py-2 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-cam-blue"
                />
              </div>
              <div className="md:col-span-2">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!canSubmit || status?.type === 'loading'}
                  className="w-full md:w-auto"
                >
                  {status?.type === 'loading' ? 'Submitting…' : 'Submit Details'}
                </Button>
              </div>
              {status && (
                <div className="md:col-span-2">
                  <p className={`text-base md:text-sm ${status.type === 'error' ? 'text-red-600' : 'text-cam-blue'}`} role="status">
                    {status.message}
                  </p>
                </div>
              )}
            </form>
          </Surface>
        </div>
      </section>
      <section className="section-container mt-8 md:mt-10">
        <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200/90 shadow-sm">
          <ContactMap address={officeAddress} zoom={17} />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-slate-600">{officeAddress}</p>
          <a
            href={`https://maps.google.com/?q=${encodeURIComponent(officeAddress)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-cam-blue hover:underline"
          >
            Open in Google Maps
          </a>
        </div>
      </section>
      <section className="section-container pb-12 md:pb-16">
        <SectionCTA title="Prefer to email us instead?" eyebrow="Still exploring?" buttonText="Email the team" />
      </section>
    </main>
  );
}
